import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useT } from '@transifex/react';
import { RaisinUIProvider } from '@raisin/components-lib';
import { ADJUST_DEEPLINK, createAuthenticatedUserContext, getAnonymousContextFromLocalStorage, getIRClickId, getUrlParamByKey, initializeAdjustForEventsWhenReady, initializeAdjustWhenReady, initializeAnonymousContext, isAuthenticated, isCSR, setCookieRegflow, setCookieState, setUpFeatureFlagUserIdentify, useFeatureFlag, } from '@raisin/cp-utils';
import { DWM_PORTFOLIOS, PRODUCT_CATEGORIES, PRODUCT_INSTANCE_STATES, ROBO_PORTFOLIOS, } from '@raisin/investments-data/api/pia_v2';
import { HeaderDataContext, HeaderNavigationDataContext, PortalContext } from '../context';
import useFetchHeaderData from '../hooks/useFetchHeaderData';
import { getCustomerMissingData, redirectCustomerBasedOnMissingData, } from '../utils/customer/checkMissingData/customerMissingData';
import { getCustomerWithFullName } from '../utils/customer/layoutMapper';
import { Footer } from './Footer';
import Header from './Header';
const LayoutWrapper = (props) => {
    var _a;
    const { locale } = useIntl();
    const t = useT();
    const [hasDWMProduct, setHasDWMProduct] = useState(false);
    const [hasMigratedRobo, setHasMigratedRobo] = useState(false);
    const { children, layout, pageTitle, seoSettings, isPortal, isRegistration, regLocale, isLogin, isCountrySelectorPage, isFinancialInformationPage, isCDVCPage, isReferenceAccountUpdatePage, isOpenbanking, isAuthRequired = false, isSmartBannerPage, isCorporate, isRaisinBankNavigationEnabled, tagline, themeVersion: pageThemeVersion, } = props;
    const { variation, isLoading } = useFeatureFlag();
    const b2cEnableAdjustSmartBannerFF = variation === null || variation === void 0 ? void 0 : variation('b2cEnableAdjustSmartBanner', false);
    const b2cEnableAdjustBannerForAuthenticatedUser = variation === null || variation === void 0 ? void 0 : variation('b2cEnableAdjustBannerForAuthenticatedUser', false);
    const b2cEnableMultiContext = variation === null || variation === void 0 ? void 0 : variation('b2cEnableMultiContext', false);
    const hideMainNavigation = !!(layout === null || layout === void 0 ? void 0 : layout.headerData.hideMainNavigation);
    const hideFooter = !!(layout === null || layout === void 0 ? void 0 : layout.footerData.hideFooter);
    const hideFooterLinks = !!(layout === null || layout === void 0 ? void 0 : layout.footerData.hideFooterLinks);
    const themeVersion = useMemo(() => {
        var _a;
        if (((_a = layout.globalSettings) === null || _a === void 0 ? void 0 : _a.themeVersion) === 'v2') {
            return 'v2';
        }
        return pageThemeVersion !== null && pageThemeVersion !== void 0 ? pageThemeVersion : undefined;
    }, [pageThemeVersion, (_a = layout.globalSettings) === null || _a === void 0 ? void 0 : _a.themeVersion]);
    const shouldHideMainNavigation = useMemo(() => hideMainNavigation, [hideMainNavigation]);
    const shouldHideFooterLinks = useMemo(() => hideFooterLinks, [hideFooterLinks]);
    const { customerData, cobranding, ppDashboardData, productAccess, productsTypes, transactionAccountData, refetchTransactionAccountData, piaProductInstances, transactionAccountError, isHeaderDataLoading, customerDataError, pomProducts, dbsDepositsAccess, } = useFetchHeaderData(layout === null || layout === void 0 ? void 0 : layout.locale);
    const shouldDisplayFooter = useMemo(() => !!Object.keys(layout.footerData).length && !hideFooter, [hideFooter, layout.footerData]);
    const hasUpgradedPortfolio = useCallback((piaProductInstances, portfolios) => (piaProductInstances === null || piaProductInstances === void 0 ? void 0 : piaProductInstances.product_instances) &&
        (piaProductInstances === null || piaProductInstances === void 0 ? void 0 : piaProductInstances.product_instances.some(({ product_category, state, instance_template_domain_id }) => product_category === PRODUCT_CATEGORIES.LIQUID &&
            portfolios.includes(instance_template_domain_id) &&
            ![PRODUCT_INSTANCE_STATES.REJECTED, PRODUCT_INSTANCE_STATES.CLOSED].includes(state))), []);
    const customerMissingData = useMemo(() => getCustomerMissingData({
        customer: customerData,
        isCDVCPage,
        isFinancialInformationPage,
        isReferenceAccountUpdatePage,
        isRegistration,
        isLogin,
        isOpenbanking,
        isCorporate,
    }), [
        customerData,
        isCDVCPage,
        isFinancialInformationPage,
        isReferenceAccountUpdatePage,
        isRegistration,
        isLogin,
        isOpenbanking,
        isCorporate,
    ]);
    if (customerMissingData) {
        redirectCustomerBasedOnMissingData(customerMissingData);
    }
    const regFlow = getUrlParamByKey('regflow');
    const state = getUrlParamByKey('state');
    useEffect(() => {
        if (isCSR() && regFlow) {
            setCookieRegflow();
        }
        if (state) {
            setCookieState();
        }
    }, [regFlow, state]);
    useEffect(() => {
        setHasDWMProduct(hasUpgradedPortfolio(piaProductInstances, DWM_PORTFOLIOS));
        setHasMigratedRobo(hasUpgradedPortfolio(piaProductInstances, ROBO_PORTFOLIOS));
    }, [hasUpgradedPortfolio, piaProductInstances]);
    useEffect(() => {
        if (isLoading)
            return;
        const irclickId = getIRClickId();
        const smartBannerDeeplink = irclickId ? ADJUST_DEEPLINK.DASHBOARD : undefined;
        // show smart banner if it is a smart banner page and user is not logged-in or
        // show smart banner for logged-in user only if it is a smart banner page and b2cEnableAdjustBannerForAuthenticatedUser is true
        const shouldShowSmartBannerInPage = (isSmartBannerPage && !isAuthenticated()) ||
            (isSmartBannerPage && isAuthenticated() && b2cEnableAdjustBannerForAuthenticatedUser);
        initializeAdjustWhenReady(b2cEnableAdjustSmartBannerFF, locale, {
            iOSDeeplinkPath: smartBannerDeeplink,
            androidDeeplinkPath: smartBannerDeeplink,
            context: irclickId,
        }, shouldShowSmartBannerInPage);
        initializeAdjustForEventsWhenReady();
    }, [
        locale,
        variation,
        isSmartBannerPage,
        b2cEnableAdjustSmartBannerFF,
        b2cEnableAdjustBannerForAuthenticatedUser,
        isLoading,
    ]);
    useEffect(() => {
        var _a;
        if (!b2cEnableMultiContext) {
            return;
        }
        initializeAnonymousContext();
        if (isAuthenticated()) {
            const anonymousContext = getAnonymousContextFromLocalStorage();
            if (customerData && anonymousContext) {
                const userContext = createAuthenticatedUserContext(customerData === null || customerData === void 0 ? void 0 : customerData.id, (_a = customerData === null || customerData === void 0 ? void 0 : customerData.address) === null || _a === void 0 ? void 0 : _a.countryCode);
                setUpFeatureFlagUserIdentify(anonymousContext, userContext);
            }
        }
    }, [b2cEnableMultiContext, customerData === null || customerData === void 0 ? void 0 : customerData.id, customerData === null || customerData === void 0 ? void 0 : customerData.address]);
    const headerData = useMemo(() => ({
        customerData: getCustomerWithFullName(customerData, t),
        cobranding,
        ppDashboardData,
        productAccess,
        productsTypes,
        transactionAccountData,
        refetchTransactionAccountData,
        piaProductInstances,
        transactionAccountError,
        isHeaderDataLoading,
        hasDWMProduct,
        hasMigratedRobo,
        customerDataError,
        pomProducts,
        dbsDepositsAccess,
    }), [
        customerData,
        cobranding,
        piaProductInstances,
        ppDashboardData,
        productAccess,
        productsTypes,
        transactionAccountData,
        refetchTransactionAccountData,
        transactionAccountError,
        hasDWMProduct,
        hasMigratedRobo,
        isHeaderDataLoading,
        customerDataError,
        pomProducts,
        dbsDepositsAccess,
        t,
    ]);
    return (React.createElement(RaisinUIProvider, { i18nMessages: props.pageMessages, version: themeVersion },
        React.createElement(PortalContext.Provider, { value: { isPortal } },
            React.createElement(HeaderDataContext.Provider, { value: headerData },
                !!Object.keys(layout.headerData).length && (React.createElement(HeaderNavigationDataContext.Provider, { value: Object.assign(Object.assign({}, layout.headerData), { hideMainNavigation: shouldHideMainNavigation, isRegistration,
                        regLocale,
                        isLogin,
                        isCountrySelectorPage,
                        isAuthRequired,
                        isOpenbanking,
                        isCorporate,
                        isRaisinBankNavigationEnabled }) },
                    React.createElement(Header, { pageTitle: pageTitle, seoSettings: seoSettings, isOpenbanking: isOpenbanking, isCountrySelectorPage: isCountrySelectorPage, isRaisinBankNavigationEnabled: isRaisinBankNavigationEnabled, tagline: tagline }))),
                children,
                shouldDisplayFooter && !isLoading ? (React.createElement(Footer, { footerData: Object.assign(Object.assign({}, layout.footerData), { hideFooterLinks: shouldHideFooterLinks }), locale: locale })) : (React.createElement(React.Fragment, null))))));
};
export default LayoutWrapper;
//# sourceMappingURL=layout-wrapper.js.map